import {
  RaceId, ShipClass, RACE_INFO, RANK_NAMES,
  t,
  raceNameKey, shipNameKey, rankNameKey,
} from '@viral-space/shared';
import {
  COLORS, FONT, GLOW, GLOW_STRONG, el, css,
  createScreenRoot, buildStarfield, createButton, createTitle,
  injectSharedKeyframes,
} from './ui-shared';
import { mountShipCardPreview, type ShipCardPreviewHandle } from './ship-card-preview';
import { isElectronDesktop } from '../input/mobile';

export interface SavedCharacter {
  id: string;
  callsign: string;
  race: RaceId;
  shipClass: ShipClass;
  level: number;
  rank: number;
  credits: number;
  lastPlayed: number;
  /**
   * All hulls owned by this character. Mirrors the server-side
   * `SavedCharacterMsg.ownedShipClasses`. The currently piloted hull
   * (`shipClass`) is always included; extra entries come from hangar
   * purchases. Optional so pre-hangar saves still deserialize cleanly.
   */
  ownedShipClasses?: ShipClass[];
}

/**
 * Optional premium / upgrade hooks. When `onUpgrade` is provided the
 * screen renders an upgrade banner (gold CTA when the account is free,
 * a quiet "PREMIUM ACTIVE" badge when it isn't). Both callbacks are
 * resolved live (`isPremium()` runs each render) so a webhook-pushed
 * `PremiumUpdate` flips the badge on the next `refreshUpgradeBanner()`
 * call from `Game`.
 */
export interface CharSelectPremiumHooks {
  isPremium: () => boolean;
  onUpgrade: () => void;
}

export class CharSelectScreen {
  private container: HTMLElement;
  private root: HTMLElement;
  private onSelect: (character: SavedCharacter) => void;
  private onCreate: () => void;
  private onSignOut: () => void;
  private username: string;
  private characters: SavedCharacter[];
  private selectedIdx = 0;
  private charCards: HTMLElement[] = [];
  private shipPreviews: ShipCardPreviewHandle[] = [];
  private premiumHooks: CharSelectPremiumHooks | null;
  private upgradeBannerEl: HTMLElement | null = null;

  constructor(
    container: HTMLElement,
    username: string,
    characters: SavedCharacter[],
    onSelect: (character: SavedCharacter) => void,
    onCreate: () => void,
    onSignOut: () => void,
    premiumHooks?: CharSelectPremiumHooks,
  ) {
    this.container = container;
    this.username = username;
    this.characters = characters;
    this.onSelect = onSelect;
    this.onCreate = onCreate;
    this.onSignOut = onSignOut;
    this.premiumHooks = premiumHooks ?? null;
    injectSharedKeyframes();
    this.root = createScreenRoot(container);
    buildStarfield(this.root);
    this.buildContent();
  }

  /** Re-render the inline upgrade banner. Called by `Game` after a
   *  `PremiumUpdate` push so the CTA flips to "PREMIUM ACTIVE" without
   *  a full screen rebuild. */
  refreshUpgradeBanner(): void {
    this.renderUpgradeBanner();
  }

  private buildContent(): void {
    const content = el('div', {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '24px',
      maxHeight: '100vh',
      overflowY: 'auto',
      width: '100%',
      animation: 'vs-fade-in 0.5s ease-out',
    });
    content.classList.add('vs-screen-content', 'vs-char-select-content');
    this.root.appendChild(content);

    content.appendChild(
      createTitle(t('char.selectTitle'), 'medium'),
    );

    const welcomeEl = el('div', {
      fontSize: '11px',
      color: COLORS.dim,
      letterSpacing: '3px',
    }, `${t('char.welcome')}, ${this.username.toUpperCase()}`);
    welcomeEl.classList.add('vs-screen-subtitle', 'vs-char-select-welcome');
    content.appendChild(welcomeEl);

    if (this.premiumHooks) {
      this.upgradeBannerEl = el('div', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        marginTop: '4px',
      });
      content.appendChild(this.upgradeBannerEl);
      this.renderUpgradeBanner();
    }

    const grid = el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: '16px',
      width: '100%',
      maxWidth: '800px',
      pointerEvents: 'auto',
    });
    grid.classList.add('vs-char-select-grid');

    for (let i = 0; i < this.characters.length; i++) {
      const ch = this.characters[i];
      const raceInfo = RACE_INFO[ch.race];
      const raceName = t(raceNameKey(ch.race));
      const shipName = t(shipNameKey(ch.shipClass));
      const pc = `#${raceInfo.primaryColor.toString(16).padStart(6, '0')}`;

      const card = el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
        background: 'rgba(0,8,0,0.7)',
        border: `1px solid ${COLORS.dim}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
      });
      card.classList.add('vs-char-select-card');

      // 3D ship preview. The actual WebGLRenderer is mounted after the
      // canvas is in the DOM (see below) so getBoundingClientRect() is
      // accurate for the initial sizing.
      const previewCanvas = document.createElement('canvas');
      previewCanvas.className = 'vs-char-select-preview-canvas';
      css(previewCanvas, {
        width: '100%',
        aspectRatio: '4 / 3',
        display: 'block',
        background:
          'radial-gradient(circle at 50% 55%, rgba(0,40,30,0.45) 0%, rgba(0,8,0,0.0) 70%)',
        borderRadius: '2px',
      });
      card.appendChild(previewCanvas);

      const callsignEl = el('div', {
        fontSize: '18px',
        color: COLORS.primary,
        textShadow: GLOW,
        letterSpacing: '3px',
      }, ch.callsign);
      callsignEl.classList.add('vs-char-select-callsign');
      card.appendChild(callsignEl);

      const infoRow = el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '10px',
      });
      infoRow.classList.add('vs-char-select-info');
      infoRow.appendChild(el('span', { color: pc }, raceName));
      infoRow.appendChild(el('span', { color: COLORS.secondary }, shipName));
      card.appendChild(infoRow);

      const statsRow = el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '10px',
        color: COLORS.dim,
      });
      statsRow.classList.add('vs-char-select-stats');
      const rankEntry = RANK_NAMES[ch.rank as keyof typeof RANK_NAMES];
      const rankName = rankEntry
        ? t(rankNameKey(ch.rank))
        : t('char.defaultRank');
      statsRow.appendChild(el('span', {}, `LVL ${ch.level}`));
      statsRow.appendChild(el('span', {}, rankName.toUpperCase()));
      statsRow.appendChild(el('span', { color: COLORS.secondary }, `${ch.credits}¢`));
      card.appendChild(statsRow);

      if (ch.lastPlayed) {
        const ago = this.timeAgo(ch.lastPlayed);
        const lastPlayedEl = el('div', {
          fontSize: '8px',
          color: COLORS.dim,
          opacity: '0.5',
          textTransform: 'none',
        }, `${t('char.lastPlayed')}: ${ago}`);
        lastPlayedEl.classList.add('vs-char-select-lastplayed');
        card.appendChild(lastPlayedEl);
      }

      const idx = i;
      card.addEventListener('click', () => this.selectChar(idx));
      card.addEventListener('dblclick', () => {
        this.selectChar(idx);
        this.launchSelected();
      });

      this.charCards.push(card);
      grid.appendChild(card);

      // Defer the WebGL mount so the canvas has a real layout size and
      // so we never block the main render path while building the grid.
      const previewShip = ch.shipClass;
      const previewPrimary = raceInfo?.primaryColor;
      const previewSecondary = raceInfo?.secondaryColor;
      const previewIdx = this.shipPreviews.length;
      this.shipPreviews.push(null as unknown as ShipCardPreviewHandle);
      requestAnimationFrame(() => {
        const handle = mountShipCardPreview(previewCanvas, {
          shipClass: previewShip,
          primaryColor: previewPrimary,
          secondaryColor: previewSecondary,
          rotating: previewIdx === this.selectedIdx,
        });
        this.shipPreviews[previewIdx] = handle;
      });
    }

    // "New character" card
    const maxChars = 5;
    if (this.characters.length < maxChars) {
      const newCard = el('div', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '16px',
        minHeight: '100px',
        background: 'rgba(0,8,0,0.4)',
        border: `1px dashed ${COLORS.dim}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
        pointerEvents: 'auto',
      });
      newCard.classList.add('vs-char-select-newcard');

      const plus = el('div', {
        fontSize: '28px',
        color: COLORS.dim,
        lineHeight: '1',
      }, '+');
      plus.classList.add('vs-char-select-newplus');
      newCard.appendChild(plus);
      newCard.appendChild(el('div', {
        fontSize: '10px',
        color: COLORS.dim,
        letterSpacing: '2px',
      }, t('char.newPilot')));

      newCard.addEventListener('mouseenter', () => {
        newCard.style.borderColor = COLORS.primary;
        newCard.style.background = 'rgba(0,255,65,0.05)';
      });
      newCard.addEventListener('mouseleave', () => {
        newCard.style.borderColor = COLORS.dim;
        newCard.style.background = 'rgba(0,8,0,0.4)';
      });
      newCard.addEventListener('click', () => this.onCreate());
      grid.appendChild(newCard);
    }

    content.appendChild(grid);

    if (this.characters.length > 0) {
      const launchBtn = createButton(
        t('char.launch'),
        () => this.launchSelected(),
      );
      launchBtn.classList.add('vs-char-select-launch');
      content.appendChild(launchBtn);
    }

    const signOutBtn = createButton(
      t('char.signOut'),
      () => this.onSignOut(),
      'ghost',
    );
    signOutBtn.classList.add('vs-char-select-signout');
    css(signOutBtn, { marginTop: '8px', opacity: '0.5', fontSize: '10px' });
    content.appendChild(signOutBtn);

    // Desktop / Steam shell only: quit the whole app. Hidden on web + mobile
    // where there is no window to close.
    if (isElectronDesktop()) {
      const exitBtn = createButton(
        t('menu.exitGame'),
        () => { window.zerionDesktop?.quit?.(); },
        'ghost',
      );
      css(exitBtn, { marginTop: '4px', opacity: '0.5', fontSize: '10px' });
      content.appendChild(exitBtn);
    }

    if (this.characters.length > 0) {
      this.selectChar(0);
    }
  }

  private renderUpgradeBanner(): void {
    const host = this.upgradeBannerEl;
    const hooks = this.premiumHooks;
    if (!host || !hooks) return;
    host.innerHTML = '';
    if (hooks.isPremium()) {
      const badge = el('div', {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        background: 'rgba(255,176,0,0.12)',
        border: `1px solid ${COLORS.secondary}`,
        color: COLORS.secondary,
        fontSize: '10px',
        letterSpacing: '3px',
        textTransform: 'uppercase',
        textShadow: `0 0 6px rgba(255,176,0,0.5)`,
      }, `★ ${t('pause.premiumActive')}`);
      host.appendChild(badge);
      return;
    }
    const cta = el('button', {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 18px',
      background: 'rgba(255,176,0,0.1)',
      border: `1px solid ${COLORS.secondary}`,
      color: COLORS.secondary,
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      pointerEvents: 'auto',
      textShadow: `0 0 6px rgba(255,176,0,0.4)`,
      transition: 'background 0.15s, color 0.15s',
    }, t('char.upgradeCta'));
    cta.addEventListener('mouseenter', () => {
      cta.style.background = COLORS.secondary;
      cta.style.color = '#000';
    });
    cta.addEventListener('mouseleave', () => {
      cta.style.background = 'rgba(255,176,0,0.1)';
      cta.style.color = COLORS.secondary;
    });
    cta.addEventListener('click', () => hooks.onUpgrade());
    host.appendChild(cta);

    const sub = el('div', {
      fontSize: '9px',
      color: COLORS.dim,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    }, t('upgrade.unlockLine'));
    host.appendChild(sub);
  }

  private selectChar(idx: number): void {
    this.selectedIdx = idx;
    for (let i = 0; i < this.charCards.length; i++) {
      if (i === idx) {
        this.charCards[i].style.border = `1px solid ${COLORS.primary}`;
        this.charCards[i].style.boxShadow = `0 0 16px rgba(0,255,65,0.3), inset 0 0 16px rgba(0,255,65,0.05)`;
      } else {
        this.charCards[i].style.border = `1px solid ${COLORS.dim}`;
        this.charCards[i].style.boxShadow = 'none';
      }
      const preview = this.shipPreviews[i];
      if (preview) preview.setRotating(i === idx);
    }
  }

  private launchSelected(): void {
    if (this.selectedIdx < this.characters.length) {
      this.onSelect(this.characters[this.selectedIdx]);
    }
  }

  private timeAgo(timestamp: number): string {
    const diff = Date.now() - timestamp;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('char.timeJustNow');
    if (mins < 60) return t('char.timeMinAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('char.timeHourAgo', { n: hours });
    const days = Math.floor(hours / 24);
    return t('char.timeDayAgo', { n: days });
  }

  show(): void {
    this.root.style.display = 'flex';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    for (const preview of this.shipPreviews) {
      if (preview) {
        try { preview.dispose(); } catch { /* swallow GL teardown errors */ }
      }
    }
    this.shipPreviews = [];
    this.root.remove();
  }
}
